import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

/**
 * Column presentation modes, like MUI's `columnVisibilityModel` but keyed by
 * `${columnKey}::${cellType}` rather than the column alone: one logical column
 * can hold cells of different types (e.g. `output.foo` may be a number list in
 * one eval and a string in another), and each type needs its own mode instead
 * of clobbering a shared slot.
 */
export type ColumnViewModeModel = Record<string, string>;

/**
 * Cell types that share a column-wide view mode; grows as cells adopt the hook.
 */
export type CellType = 'number-list' | 'string';

// Field-path column keys use `.`, so `::` is a collision-free separator.
const MODEL_KEY_SEP = '::';

type ColumnViewModeContextValue = {
  model: ColumnViewModeModel;
  setColumnViewMode: (modelKey: string, mode: string) => void;
};

const ColumnViewModeContext = createContext<ColumnViewModeContextValue | null>(
  null
);

type ColumnViewModeProviderProps = {
  /** Controlled model; omit to fall back to the provider's own uncontrolled state. */
  model?: ColumnViewModeModel;
  onModelChange?: (model: ColumnViewModeModel) => void;
  children: React.ReactNode;
};

/**
 * Shares the view-mode model with the cells beneath it — a context because
 * those cells are rendered deep in the grid.
 */
export const ColumnViewModeProvider = ({
  model,
  onModelChange,
  children,
}: ColumnViewModeProviderProps) => {
  const [internalModel, setInternalModel] = useState<ColumnViewModeModel>({});
  const isControlled = model !== undefined;
  const resolvedModel = isControlled ? model : internalModel;

  const setColumnViewMode = useCallback(
    (modelKey: string, mode: string) => {
      if (isControlled) {
        onModelChange?.({...model, [modelKey]: mode});
      } else {
        setInternalModel(prev => ({...prev, [modelKey]: mode}));
      }
    },
    [isControlled, model, onModelChange]
  );

  const value = useMemo(
    () => ({model: resolvedModel, setColumnViewMode}),
    [resolvedModel, setColumnViewMode]
  );

  return (
    <ColumnViewModeContext.Provider value={value}>
      {children}
    </ColumnViewModeContext.Provider>
  );
};

/**
 * Column-wide view mode when a `ColumnViewModeProvider` and `columnKey` are
 * present (keyed per `cellType`, so types in one column don't clobber each
 * other), else per-cell local state.
 *
 * Returns `[mode, setMode, isShared]`; gate "applies to whole column" UI on
 * `isShared`, not `columnKey` alone. `allowedModes` guards a stale or foreign
 * stored mode — anything outside it falls back to `defaultMode`.
 */
export const useColumnOrLocalViewMode = <T extends string>(
  columnKey: string | undefined,
  cellType: CellType,
  defaultMode: T,
  allowedModes: readonly T[]
): [T, (mode: T) => void, boolean] => {
  const ctx = useContext(ColumnViewModeContext);
  const [localMode, setLocalMode] = useState<T>(defaultMode);

  if (ctx && columnKey) {
    const modelKey = `${columnKey}${MODEL_KEY_SEP}${cellType}`;
    const stored = ctx.model[modelKey];
    const mode =
      stored != null && (allowedModes as readonly string[]).includes(stored)
        ? (stored as T)
        : defaultMode;
    return [mode, (next: T) => ctx.setColumnViewMode(modelKey, next), true];
  }
  return [localMode, setLocalMode, false];
};
